// Cosmetic animation of a confirmed server outcome. No RNG or wallet state.
export const faceAngle = side => side === 'tails' ? 180 : 0;
const REST_TILT = 18, REST_YAW = -24;
export const restingPose = side => `rotateX(${faceAngle(side) + REST_TILT}deg) rotateY(${REST_YAW}deg)`;
export function tossFrames(from, to, liftPx = 125) {
  if (![from, to].every(side => side === 'heads' || side === 'tails')) throw new Error('Invalid coin face');
  const start = faceAngle(from), end = 7 * 360 + faceAngle(to);
  const turn = (offset, degrees, yaw = REST_YAW, roll = 0) => ({ offset, transform: `rotateX(${degrees + REST_TILT}deg) rotateY(${yaw}deg) rotateZ(${roll}deg)` });
  return {
    finalAngle: end,
    coin: [turn(0, start), turn(.16, start + 410, 10, -9), turn(.39, start + 1180, -8, 8), turn(.64, end - 590, 14, -5),
      turn(.81, end - 110, -18, 4), turn(.91, end + 14, REST_YAW, -2), turn(.96, end - 5), turn(1, end)],
    lift: [{ offset: 0, transform: 'translateY(0) scale(1)' }, { offset: .16, transform: `translateY(${-liftPx * .73}px) scale(1.03)`, easing: 'ease-out' },
      { offset: .39, transform: `translateY(${-liftPx}px) scale(1.08)`, easing: 'ease-in' }, { offset: .81, transform: 'translateY(0) scale(1)' },
      { offset: .90, transform: 'translateY(-12px) scale(1.008)' }, { offset: 1, transform: 'translateY(0) scale(1)' }],
    shadow: [{ offset: 0, transform: 'scale(1)', opacity: .6 }, { offset: .39, transform: 'scale(.5)', opacity: .2 },
      { offset: .81, transform: 'scale(1.17)', opacity: .72 }, { offset: .90, transform: 'scale(.9)', opacity: .5 }, { offset: 1, transform: 'scale(1)', opacity: .6 }],
  };
}
const abortError = () => new DOMException('Coin toss cancelled', 'AbortError');
const wait = (ms, signal) => new Promise((resolve, reject) => {
  let timer; const cancel = () => { clearTimeout(timer); reject(abortError()); };
  if (signal?.aborted) return cancel();
  signal?.addEventListener('abort', cancel, { once: true });
  timer = setTimeout(() => { signal?.removeEventListener('abort', cancel); resolve(); }, ms);
});
export function showFace(coin, side) { coin.style.transform = restingPose(side); }
export async function playToss({ coin, lift, shadow, ring, scene }, from, to, { reduced = false, signal } = {}) {
  if (signal?.aborted) throw abortError();
  const frames = tossFrames(from, to, Math.min(125, Math.max(65, (scene.clientHeight || 400) * .29)));
  if (!coin.animate) { await wait(reduced ? 180 : 450, signal); showFace(coin, to); return; }
  const options = { duration: reduced ? 240 : 2200, easing: 'linear', fill: 'forwards' };
  if (reduced) showFace(coin, to);
  const animations = reduced ? [coin.animate([{ opacity: .45 }, { opacity: 1 }], options)] :
    [coin.animate(frames.coin, options), lift.animate(frames.lift, options), shadow.animate(frames.shadow, options)];
  const cancel = () => animations.forEach(animation => animation.cancel());
  signal?.addEventListener('abort', cancel, { once: true });
  try {
    await Promise.all(animations.map(animation => animation.finished));
    if (signal?.aborted) throw abortError();
    showFace(coin, to);
  } finally {
    signal?.removeEventListener('abort', cancel); animations.forEach(animation => animation.cancel());
  }
  if (!reduced && ring?.animate) {
    const ripple = ring.animate([{ opacity: .5, transform: 'scale(.78)' }, { opacity: 0, transform: 'scale(1.16)' }], { duration: 460, easing: 'ease-out' });
    ripple.finished.catch(() => {});
  }
}
